import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowCircleRight } from '../../../assets/icons/arrow-circle-right.svg';
import { ReactComponent as CheckBoxActive } from '../../../assets/icons/check-box-active-icon.svg';
import { ReactComponent as CheckBoxInActive } from '../../../assets/icons/check-box-icon.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  EditVlbcInterface,
  DeviceListInterface,
  DeviceInterface,
} from '../../../interface/vlbc.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import {
  fetchChargingStations,
  fetchDevices,
  fetchVlbcs,
  updateDevices,
} from '../../../redux/slice/VlbcSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import vlbcApi from '../../../utils/api/vlbc';

interface EditVlbcProperties {
  handleCancel: () => void;
  closeVlbcModal: () => void;
  vlbcId: string;
  fetchChargersAssignCount: () => void;
}

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}
/**
 * Edit vlbc component
 */
function EditVlbc(properties: EditVlbcProperties) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('vlbc');
  const { handleCancel, closeVlbcModal, vlbcId, fetchChargersAssignCount } = properties;
  const { devices, chargingStations } = useAppSelector((states) => states.vlbc);
  const [editApiLoading, setEditApiLoading] = useState<boolean>(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });

  const algorithms = [
    {
      label: t('equalSharing'),
      value: 'Equal Sharing',
    },
  ];

  const loadBalancerType = [
    {
      label: t('dynamicLoadBalancing'),
      value: 'Dynamic Load Balancing',
    },
    {
      label: t('staticLoadBalancing'),
      value: 'Static Load Balancing',
    },
  ];

  const [vlbcForm, setVlbcForm] = useState<EditVlbcInterface>({
    loadBalancerName: '',
    maxPowerInAmps: '',
    algorithm: algorithms[0].value,
    chargingStationId: '',
    loadBalancerType: loadBalancerType[0].value,
  });

  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });

  const [selectedDevices, setSelectedDevices] = useState<DeviceListInterface[]>([]);

  /**
   * Function to handle the checkbox click event
   */
  const toggleDeviceLeft = (index: number) => {
    const updatedDevices = devices.data.map((device, index_) => {
      if (index_ === index) {
        return { ...device, isSelected: !device.isSelected };
      }
      return device;
    });

    dispatch(updateDevices(updatedDevices));
  };

  /**
   * Function to handle the checkbox click event
   */
  const toggleDeviceRight = (index: number) => {
    const selectedDevicesTemporary = selectedDevices.map((device, index_) => {
      if (index_ === index) {
        return { ...device, isSelected: !device.isSelected };
      }
      return device;
    });

    setSelectedDevices(selectedDevicesTemporary);
  };

  /**
   * Renders the custom data for the serial number column
   */
  const renderSerialNumber = (deviceList: DeviceListInterface, index: number) => (
    <div className="table-row-each-data select-row">
      <div className="check-box" onClick={() => toggleDeviceLeft(index)} role="presentation">
        <div className="check-box-inner">
          {deviceList.isSelected ? <CheckBoxActive /> : <CheckBoxInActive />}
        </div>
      </div>
      {index + 1}
    </div>
  );

  /**
   * Renders the custom data for the serial number column
   */
  const renderSerialNumberSelectedTable = (deviceList: DeviceListInterface, index: number) => (
    <div className="table-row-each-data select-row">
      <div className="check-box" onClick={() => toggleDeviceRight(index)} role="presentation">
        <div className="check-box-inner">
          {deviceList.isSelected ? <CheckBoxActive /> : <CheckBoxInActive />}
        </div>
      </div>
      {index + 1}
    </div>
  );

  const column = [
    {
      label: t('SNo'),
      accessorKey: 'sno' as keyof DeviceListInterface,
      customData: renderSerialNumber,
    },
    {
      label: t('deviceName'),
      accessorKey: 'chargingPointName' as keyof DeviceListInterface,
      sortAllow: false,
    },

    {
      label: t('noOfConnectors'),
      accessorKey: 'noOfConnectors' as keyof DeviceListInterface,
      sortAllow: false,
    },
    {
      label: t('totalPower'),
      accessorKey: 'maximumOutputPower' as keyof DeviceListInterface,
      sortAllow: false,
    },
  ];

  const selectedTableColumn = [
    {
      label: t('SNo'),
      accessorKey: 'sno' as keyof DeviceListInterface,
      customData: renderSerialNumberSelectedTable,
    },
    {
      label: t('deviceName'),
      accessorKey: 'chargingPointName' as keyof DeviceListInterface,
      sortAllow: false,
    },

    {
      label: t('noOfConnectors'),
      accessorKey: 'noOfConnectors' as keyof DeviceListInterface,
      sortAllow: false,
    },
    {
      label: t('totalPower'),
      accessorKey: 'maximumOutputPower' as keyof DeviceListInterface,
      sortAllow: false,
    },
  ];

  /**
   * Handle the input change function
   * @param name
   * @param value
   */
  const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
    setVlbcForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle the move selected devices function
   */
  const moveForward = () => {
    const selectedDevicesTemporary = devices.data.filter((device) => device.isSelected);

    if (!selectedDevicesTemporary.length) return;
    setSelectedDevices([...selectedDevicesTemporary, ...selectedDevices]);

    const unSelectedDevicesTemporary = devices.data.filter((device) => !device.isSelected);
    dispatch(updateDevices(unSelectedDevicesTemporary));
  };

  /**
   * Handle the move selected devices back to main device list
   */
  const moveBackward = () => {
    const selectedDevicesTemporary = selectedDevices.filter((device) => device.isSelected);

    if (!selectedDevicesTemporary.length) return;

    // Add selected devices back to the global list
    const updatedDeviceList = [...selectedDevicesTemporary, ...devices.data];

    dispatch(updateDevices(updatedDeviceList));

    const unSelectedDevicesTemporary = selectedDevices.filter((device) => !device.isSelected);

    setSelectedDevices(unSelectedDevicesTemporary);
  };

  /**
   * Navigate back
   */
  const handleNavigateBack = () => {
    setVlbcForm({
      loadBalancerName: '',
      maxPowerInAmps: '',
      algorithm: algorithms[0].value,
      chargingStationId: '',
      loadBalancerType: loadBalancerType[0].value,
    });

    setSelectedDevices([]);
    setModalBoxStatus('close');
    closeVlbcModal();
  };

  /**
   * Submit Vlbc
   */
  const submit = () => {
    const chargingPointSns = selectedDevices
      .filter((device) => device.isSelected)
      .map((device) => device.chargingPointSn);

    if (
      vlbcForm.loadBalancerName &&
      vlbcForm.maxPowerInAmps &&
      vlbcForm.loadBalancerType &&
      vlbcForm.algorithm &&
      vlbcForm.chargingStationId
    ) {
      setFormStatus({
        showFormErrors: false,
        buttonLoader: true,
      });

      const request: any = {
        loadBalancerName: vlbcForm.loadBalancerName,
        maximumOutputPower: vlbcForm.maxPowerInAmps,
        loadBalancerType: vlbcForm.loadBalancerType,
        algorithm: vlbcForm.algorithm,
        chargingStation: vlbcForm.chargingStationId,
        chargingPoints: chargingPointSns,
      };

      vlbcApi
        .updateVlbc(vlbcId, request)
        .then(() => {
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });

          setModalBoxStatus('open');
          setModalBoxDetail({
            title: null,
            content: (
              <SuccessNotification
                title={t('loadBalancerUpdatedSuccessfully')}
                description=""
                notificationCloseTimer={3000}
                onNotificationClose={handleNavigateBack}
              />
            ),
            notificationModal: true,
          });

          dispatch(
            fetchVlbcs({
              page: DefaultApiLimit.page,
              limit: DefaultApiLimit.limit,
            }),
          );
          fetchChargersAssignCount();
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: true,
            buttonLoader: false,
          });
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || 'Something went wrong!',
              },
            }),
          );
        });
    } else {
      setFormStatus({
        showFormErrors: true,
        buttonLoader: false,
      });
    }
  };

  useEffect(() => {
    if (!devices.loading && vlbcForm.chargingStationId) {
      dispatch(fetchDevices(vlbcForm.chargingStationId));
    }
  }, [vlbcForm.chargingStationId]);

  /**
   * Fetch Vlbc
   * @param vlbcId
   */
  const fetchVlbc = async (id: string) => {
    setEditApiLoading(true);
    await vlbcApi
      .fetchVlbc(id)
      .then((response) => {
        const vlbcInfo = response.data;

        setVlbcForm({
          loadBalancerName: vlbcInfo.loadBalancerName,
          loadBalancerType: vlbcInfo.loadBalancerType,
          maxPowerInAmps: vlbcInfo.maxPowerInAmps,
          algorithm: vlbcInfo.algorithm,
          chargingStationId: vlbcInfo.chargingStationId,
        });

        vlbcInfo.chargingPoints = vlbcInfo.chargingPoints.map(
          (chargingPoint: DeviceListInterface) => {
            return {
              ...chargingPoint,
              isSelected: true,
            };
          },
        );

        setSelectedDevices(vlbcInfo.chargingPoints);
        setEditApiLoading(false);
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
        setEditApiLoading(false);
      });
  };

  useEffect(() => {
    dispatch(fetchChargingStations());
  }, []);

  useEffect(() => {
    if (vlbcId) {
      fetchVlbc(vlbcId);
    }
  }, [vlbcId]);

  useEffect(() => {
    const unmatchedDevices = devices.data.filter(
      (device: DeviceInterface) =>
        !selectedDevices.some(
          (cp: DeviceInterface) => cp.chargingPointSn === device.chargingPointSn,
        ),
    );

    if (devices.data.length !== unmatchedDevices.length) {
      dispatch(updateDevices(unmatchedDevices));
    }
  }, [devices, selectedDevices]);
  return (
    <div className="add-vlbc-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />

      {(() => {
        let cardview = null;

        if (editApiLoading) {
          cardview = (
            <div className="table-loading">
              <BatteryLoader />
            </div>
          );
        } else if (!editApiLoading) {
          cardview = (
            <div className="add-vlbc-container">
              <div className="top-form">
                <CustomInput
                  name="loadBalancerName"
                  inputLabel={t('loadBalancerName')}
                  type="text"
                  placeholder={t('enterTheLoadBalancerName')}
                  value={vlbcForm.loadBalancerName}
                  onChangeValue={handleInputChange}
                  valueRequired
                  error={!vlbcForm.loadBalancerName && formStatus.showFormErrors}
                  errorMessage={t('loadBalancerIsRequired')}
                />
                <CustomInput
                  name="maxPowerInAmps"
                  inputLabel="Max Power (Amps)"
                  type="number"
                  placeholder={t('enterTheMaxPowerInAmps')}
                  value={vlbcForm.maxPowerInAmps}
                  onChangeValue={handleInputChange}
                  valueRequired
                  error={!vlbcForm.maxPowerInAmps && formStatus.showFormErrors}
                  errorMessage={t('maxPowerAmpsIsRequired')}
                />
                <SelectField
                  inputLabel={t('loadBalancerType')}
                  name="loadBalancerType"
                  placeholder={t('selectLoadBalancerType')}
                  selectedValue={vlbcForm.loadBalancerType}
                  onChangeValue={handleInputChange}
                  options={loadBalancerType}
                  valueRequired
                  error={!vlbcForm.loadBalancerType && formStatus.showFormErrors}
                  errorMessage={t('loadBalancerTypeIsRequired')}
                />
                <SelectField
                  inputLabel={t('algorithm')}
                  name="algorithm"
                  placeholder={t('selectAlgorithm')}
                  selectedValue={vlbcForm.algorithm}
                  onChangeValue={handleInputChange}
                  options={algorithms}
                  valueRequired
                  error={!vlbcForm.algorithm && formStatus.showFormErrors}
                  errorMessage={t('algorithmIsRequired')}
                />
              </div>
              <div className="device-selection-wrap">
                <div className="first-section">
                  <SelectField
                    inputLabel=""
                    name="chargingStationId"
                    placeholder={t('selectStation')}
                    selectedValue={vlbcForm.chargingStationId}
                    onChangeValue={handleInputChange}
                    options={chargingStations.data}
                    valueRequired
                  />
                  <div className="table-container-page table-container-page-1">
                    <div className="table-count">
                      {t('deviceList')} ({devices.data.length}){' '}
                    </div>

                    <CustomTable
                      sizeChangeButtonRequired={false}
                      loading={devices.loading}
                      header={column}
                      data={devices.data}
                      currentPage={1}
                      handlePageChange={() => {}}
                      handleLimitChange={() => {}}
                      setHeightAutoToTr
                      filterAllow={false}
                    />
                  </div>
                </div>
                <div className="mover-wrap">
                  <div className="mover">
                    <span onClick={moveForward} role="presentation">
                      <ArrowCircleRight />
                    </span>
                    <span>
                      <ArrowCircleRight onClick={moveBackward} className="arrow-left" />
                    </span>
                  </div>
                </div>
                <div className="second-section">
                  <div className="table-container-page table-container-page-2">
                    <div className="table-count">
                      {t('selectedDeviceList')} ({selectedDevices.length}){' '}
                    </div>
                    <CustomTable
                      sizeChangeButtonRequired={false}
                      loading={false}
                      header={selectedTableColumn}
                      data={selectedDevices}
                      currentPage={1}
                      handlePageChange={() => {}}
                      handleLimitChange={() => {}}
                      setHeightAutoToTr
                      filterAllow={false}
                    />
                  </div>
                </div>
              </div>
              <div className="add-vlbc-form-footer">
                <div className="back-button-container">
                  <MyButton label="Cancel" buttonType="back" onClickFunc={handleCancel} />
                </div>
                <div className="submit-button-container">
                  <MyButton
                    label={t('update')}
                    buttonType="submit"
                    onClickFunc={submit}
                    disableButton={formStatus.buttonLoader}
                    loading={formStatus.buttonLoader}
                  />
                </div>
              </div>
            </div>
          );
        }

        return cardview;
      })()}
    </div>
  );
}

export default EditVlbc;
